import type { SignupStatus } from '../../common/hooks/use-signup-api.hook';

/**
 * The self-serve statuses that keep the app closed (docs/plg-review.md):
 * still connecting (`pending_embed`), waiting for a person at Ardealy to look
 * (`pending_review`), or turned down (`blocked`).
 */
export const GATED_STATUSES = ['pending_embed', 'pending_review', 'blocked'] as const;

const gated = (s?: string | null) => (GATED_STATUSES as readonly string[]).includes(String(s || ''));

/**
 * Whether the signed-in vendor is held at the gate before the app opens.
 * Only self-serve accounts ever are: every other vendor has no `selfServe`,
 * and a status we could not read is not a reason to lock anyone out.
 */
export const needsActivation = (status?: SignupStatus | null): boolean => Boolean(status?.selfServe && gated(status.status));

/** Waiting for the review, or turned down: the steps are done, the decision is not. */
export const inReview = (status?: SignupStatus | null): boolean => Boolean(status?.selfServe && status.status === 'pending_review');
export const isBlocked = (status?: SignupStatus | null): boolean => Boolean(status?.selfServe && status.status === 'blocked');

/**
 * The gate lets go. Both halves done on an API without the review step (it
 * answers `active` then, but a status read a moment early may still say
 * `pending_embed`); never while a review is pending or the account is blocked.
 */
export const isActivated = (status?: SignupStatus | null): boolean =>
    !status ||
    !status.selfServe ||
    status.status === 'active' ||
    (status.status === 'pending_embed' && Boolean(status.sdkConnected && status.emailVerified));

/** A hint on the signed-in user, so the app is never painted before the gate. */
export const userLooksPending = (user: any): boolean => gated(user?.vendor?.selfServe?.status);

/** How often the review screen asks whether the decision is in. */
export const REVIEW_POLL_MS = 30_000;

/** How often the step looks for the snippet while the tab is visible. */
export const INSTALL_POLL_MS = 15_000;

/** A reason code from the check, normalised to an i18n key suffix ("snippet_not_found" → "SNIPPET_NOT_FOUND"). */
export const reasonKey = (reason?: string | null): string | null => {
    const r = String(reason || '').trim();
    if (!r) return null;
    return r.replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '').toUpperCase() || null;
};

/** Reasons we have words for; any other falls back to the generic "not found yet". */
export const KNOWN_REASONS = ['NOT_FOUND', 'SNIPPET_NOT_FOUND', 'WRONG_ALIAS', 'WRONG_VENDOR', 'UNREACHABLE', 'TIMEOUT', 'BLOCKED', 'NO_DOMAIN'] as const;
